import { useMemo, useCallback } from "react";
import { normalizeValue } from "./utils";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelectedChips,
  type EntitySelectorConfig,
  type EntitySelectorVariant,
  type EntitySelection,
} from "@salestrak/ui";
import { useCustomFieldPool } from "./hooks";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

export type CustomFieldSelectorProps = Omittable<{
  value?: string | string[] | null;
  onValueChange?: (ids: string[]) => void;
  /** Custom field IDs to exclude (e.g., already-assigned fields) */
  exclude?: string[];
  maxSelections?: number;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}>;

export function CustomFieldSelector({
  value,
  onValueChange,
  exclude,
  maxSelections,
  variant,
  placeholder = "Select fields...",
  size,
  disabled,
  onOpenChange,
  children,
}: CustomFieldSelectorProps) {
  const { pool } = useCustomFieldPool({ exclude });

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections,
      searchPlaceholder: "Search fields...",
      emptyMessage: "No fields found",
      noItemsMessage: "No custom fields yet",
    }),
    [pool, maxSelections],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const ids = normalizeValue(value);
    if (ids.length === 0) return [];
    const itemMap = new Map(pool.items.map((item) => [item.id, item]));
    return ids
      .map((id) => {
        const entity = itemMap.get(id);
        if (!entity) return null;
        return { id, poolId: pool.id, entity };
      })
      .filter(Boolean) as EntitySelection[];
  }, [value, pool]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange?.(next.map((s) => s.id));
    },
    [onValueChange],
  );

  return (
    <EntitySelector
      variant={variant}
      config={config}
      value={selections}
      onValueChange={handleValueChange}
      onOpenChange={onOpenChange}
    >
      {children ?? (
        <>
          <EntitySelectorTrigger
            placeholder={placeholder}
            size={toEntityTriggerSize(size)}
            disabled={disabled}
          />
          <EntitySelectorContent>
            <EntitySelectorInput />
            <EntitySelectorSelectedChips label="Selected" />
            <EntitySelectorPool value={pool.id} />
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}
